import React, { useEffect } from "react";
import { Route, Redirect } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Switch, useLocation } from "react-router-dom";

import Tap from "@tapfiliate/tapfiliate-js";

import { TAPFILIATE_ACCOUNT_ID } from "../config";
import AdminHtml from "../layouts/AdminHtml";
import LogOutPage from "../pages/LogOutPage";
import ActivityHeartbeat from "src/components/ActivityHeartbeat";
import ImpersonationGrantWatcher from "src/components/ImpersonationGrantWatcher";

import ManagementPage from "../pages/ManagementPage";
import TransactionDetailsPage from "src/pages/TransactionDetails";
import TransactionDetailsAllPage from "src/pages/TransactionDetails/All";
import TransactionDetailsInventoryPage from "src/pages/TransactionDetails/Inventory";
import TransactionDetailsSalesPage from "src/pages/TransactionDetails/Sales";
import TransactionDetailsRefundsPage from "src/pages/TransactionDetails/Sales/Refunds";
import TransactionDetailsExpensesPage from "src/pages/TransactionDetails/Expenses";
import TransactionDetailsOtherPage from "src/pages/TransactionDetails/Other";
import TransactionDetailsPageRedirect from "src/pages/TransactionDetails/Redirect";
import ReconciliationPage from "../pages/Reconciliation";
import ReportsPage from "../pages/ReportsPage";
import IntegrationsPage from "../pages/Integrations";

import AddInventoryDialog from "../pages/RootDialogs/AddInventoryDialog";
import EnterPaymentMethodDialog from "../pages/RootDialogs/EnterPaymentMethodDialog";
import EmptyTablesDialog from "../pages/RootDialogs/EmptyTablesDialog";
import DuplicateInventoryChecker from "../pages/RootDialogs/DuplicateInventoryChecker";
import ReportSaleDialog from "../pages/RootDialogs/ReportSaleDialog";
import ManageUploadsDialog from "../pages/RootDialogs/Uploads/ManageUploadsDialog";
import SalesMatching from "src/pages/RootDialogs/SalesMatching";
import SalesReview from "../pages/RootDialogs/SalesReview";

import WalkthroughDialog from "../pages/RootDialogs/WalkthroughDialog";
import UpgradePlanDialog from "src/pages/RootDialogs/UpgradePlanDialog";
import AddExpenseDialog from "src/pages/RootDialogs/AddExpenseDialog";
import MaintenanceActivitiesDialog from "src/pages/RootDialogs/MaintenanceActivitiesDialog";
import WalkThroughNotificationsIconDialog from "src/pages/RootDialogs/WalkThroughNotificationsIconDialog";
import IntegrationSyncerDialog from "src/pages/RootDialogs/IntegrationSyncerDialog";
import PoshmarkDeductionsDialog from "src/pages/RootDialogs/PoshmarkDeductionsDialog";
import AddJournalDialog from "src/pages/RootDialogs/AddJournalDialog";
import EditTransactionDialog from "src/pages/RootDialogs/EditTransactionDialog";
import EditSplitDialog from "src/pages/RootDialogs/EditSplitDialog";
import EditInventoryDialog from "src/pages/RootDialogs/EditInventoryDialog";
import EditSaleDialog from "src/pages/RootDialogs/EditSaleDialog";

import { activeDialogSelector } from "../store/adminHtml/selector";
import {
  tapfiliateSelector,
  userCustomerIdSelector,
  selectUser,
  userQualifyingQuestionsSelector,
} from "../store/system/selector";
import CategorizeExpensesDialog from "../pages/RootDialogs/CategorizeExpensesDialog";
import AcceptTermsDialog from "../pages/RootDialogs/AcceptTermsDialog";
import AddGeneralLedgerAccountDialog from "../pages/RootDialogs/AddGeneralLedgerAccountDialog";
import ReviewReturnsDialog from "src/pages/RootDialogs/ReviewReturnsDialog";

import { userGetStartedHasFinishedSelector } from "src/store/system/selector";
import { userChecklistCompletedSelector } from "src/store/system/selector";

import AppLoader, { AppLoaderWithEscalation } from "src/components/AppLoader";

import useUserDataLoader from "src/utils/useUserDataLoader";
import { useSalesTransactionsDataLoader } from "src/utils/userData/useSalesTransactionsDataLoader";
import { clearSignupStatus } from "src/store/system/actions";
import { clearStoredReferral, getStoredReferralCode } from "src/utils/referral";
import ListItem from "@material-ui/core/ListItem";
import { Link as MuiLink, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import theme from "src/theme";
import BankAccountsInitialPopulation from "src/pages/Reconciliation/InitialPopulation";
import QualifyingQuestionsPage from "src/pages/Onboarding/QualifyingQuestionsPage";
import GetStarted from "src/pages/GetStarted/GetStarted";
import GlobalTour from "src/components/GlobalTour";

const useStyles = makeStyles({
  singlePaddingRight: {
    paddingRight: theme.spacing(1),
  },
  footerRow: {
    textAlign: "center",
    justifyContent: "center",
  },
  footerDisclaimerRow: {
    paddingBottom: 4,
  },
  footerLinksRow: {
    paddingBottom: 10,
  },
});

function DefaultRedirection() {
  const hasFinished = useSelector(userGetStartedHasFinishedSelector);
  const checklistCompleted = useSelector(userChecklistCompletedSelector);
  const user = useSelector(selectUser);
  const qualifyingQuestions = useSelector(userQualifyingQuestionsSelector);
  if (!user) return null;
  if (
    user &&
    user.createdAt &&
    user.createdAt.toDate() > new Date("2026-05-14") &&
    !qualifyingQuestions
  ) {
    return <Redirect to="/initial_questions" />;
  }
  return (
    <Redirect
      to={hasFinished || checklistCompleted ? "/management" : "/get-started"}
    />
  );
}

const PrivateRouter = () => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const activeDialog = useSelector(activeDialogSelector);
  const tapfiliateReady = useSelector(tapfiliateSelector);
  const userCustomerId = useSelector(userCustomerIdSelector);
  const { isCoreUserDataReady, isAuxiliaryUserDataReady } = useUserDataLoader();
  const salesTransactionsUi =
    useSalesTransactionsDataLoader(isCoreUserDataReady);

  useEffect(() => {
    if (!userCustomerId || !tapfiliateReady) return;
    const referralCode = getStoredReferralCode();
    if (referralCode) {
      // Tapfiliate drops a trial reported before this click returns a visitor
      // id, which a new signup never has yet, so report it from the callback.
      Tap.init(
        TAPFILIATE_ACCOUNT_ID,
        { integration: "stripe" },
        null,
        { referral_code: referralCode, always_callback: true },
        (error: unknown) => {
          clearStoredReferral();
          if (error) {
            console.warn("Tapfiliate referral click failed", error);
            return;
          }
          Tap.trial(userCustomerId);
        }
      );
    }
    dispatch(clearSignupStatus());
  }, [userCustomerId, tapfiliateReady, dispatch]);

  if (salesTransactionsUi.shellBlockingLoader || !isAuxiliaryUserDataReady)
    return <AppLoaderWithEscalation />;

  return (
    <BankAccountsInitialPopulation fallback={<AppLoader />}>
      <ActivityHeartbeat />
      <ImpersonationGrantWatcher />
      <Switch>
        <Route exact path="/logout" component={LogOutPage} />
        <Route
          exact
          path="/initial_questions"
          component={QualifyingQuestionsPage}
        />
        <Route path="/get-started" exact>
          <GetStarted />
        </Route>
        <Route path="/management" exact>
          <AdminHtml
            pageTitle="Dashboard"
            pageDescription="Insights at a glance"
          >
            <ManagementPage />
          </AdminHtml>
        </Route>
        <Route path="/transaction-details">
          <AdminHtml
            pageTitle="Transaction Details"
            pageDescription="View lists of your transactions"
          >
            <TransactionDetailsPage>
              <Switch>
                <Route path="/transaction-details/all" exact>
                  <TransactionDetailsAllPage />
                </Route>
                <Route path="/transaction-details/inventory" exact>
                  <TransactionDetailsInventoryPage />
                </Route>
                <Route path="/transaction-details/sales" exact>
                  <TransactionDetailsSalesPage />
                </Route>
                <Route path="/transaction-details/refunds" exact>
                  <TransactionDetailsRefundsPage />
                </Route>
                <Route path="/transaction-details/expenses" exact>
                  <TransactionDetailsExpensesPage />
                </Route>
                <Route path="/transaction-details/other" exact>
                  <TransactionDetailsOtherPage />
                </Route>
                <Route>
                  <TransactionDetailsPageRedirect />
                </Route>
              </Switch>
            </TransactionDetailsPage>
          </AdminHtml>
        </Route>
        <Route path="/reconciliation" exact>
          <AdminHtml
            pageTitle="Reconciliation"
            pageDescription="Verify your data entry"
          >
            <ReconciliationPage />
          </AdminHtml>
        </Route>
        <Route path="/reports" exact>
          <AdminHtml
            pageTitle="Reseller Reports"
            pageDescription="Compile your business data"
          >
            <ReportsPage />
          </AdminHtml>
        </Route>
        <Route path="/integrations" exact>
          <IntegrationsPage />
        </Route>
        <Route component={DefaultRedirection} />
      </Switch>
      <WalkthroughDialog open={activeDialog === "walkthrough"} />
      <ReportSaleDialog open={activeDialog === "report_sale"} />
      <AddInventoryDialog open={activeDialog === "add_inventory"} />
      <ManageUploadsDialog open={activeDialog === "manage_uploads"} />
      <EmptyTablesDialog open={activeDialog === "empty_tables"} />
      <SalesMatching open={activeDialog === "match_sales"} />
      <SalesReview open={activeDialog === "review_sales"} />
      <DuplicateInventoryChecker open={activeDialog === "inventory_sync"} />
      <CategorizeExpensesDialog open={activeDialog === "categorize_expenses"} />
      <AcceptTermsDialog open={activeDialog === "accept_terms"} />
      <ReviewReturnsDialog open={activeDialog === "returns_review"} />
      <AddGeneralLedgerAccountDialog open={activeDialog === "add_gl_account"} />
      <UpgradePlanDialog open={activeDialog === "upgrade_plan"} />
      <EnterPaymentMethodDialog />
      {/*<SetUpFeatureDialog />*/}
      <MaintenanceActivitiesDialog
        open={activeDialog === "maintenance_activities_dialog"}
      />
      <AddExpenseDialog open={activeDialog === "add_expense"} />
      <WalkThroughNotificationsIconDialog
        open={activeDialog === "walk_through_notifications_icon"}
      />
      <IntegrationSyncerDialog />
      <PoshmarkDeductionsDialog open={activeDialog === "poshmark_deductions"} />
      <PoshmarkDeductionsDialog
        open={activeDialog === "poshmark_update_shipping"}
        cancel
      />
      <AddJournalDialog open={activeDialog === "add_journal"} />
      <EditTransactionDialog open={activeDialog === "edit_transaction"} />
      <EditSplitDialog open={activeDialog === "edit_split"} />
      <EditInventoryDialog open={activeDialog === "edit_inventory"} />
      <EditSaleDialog open={activeDialog === "edit_sale"} />
      {location.pathname !== "/initial_questions" && (
        <>
          {location.pathname !== "/logout" ? (
            <ListItem
              className={`${classes.footerRow} ${classes.footerDisclaimerRow}`}
            >
              <Typography variant="caption">
                The term 'Etsy' is a trademark of Etsy, Inc. This Application
                uses Etsy's API, but is not endorsed or certified by Etsy.
              </Typography>
            </ListItem>
          ) : null}

          <ListItem
            className={`${classes.footerRow} ${classes.footerLinksRow}`}
          >
            <MuiLink
              href="https://www.myresellergenie.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.singlePaddingRight}
            >
              Terms of Use
            </MuiLink>
            <MuiLink
              href="https://www.myresellergenie.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </MuiLink>
          </ListItem>
        </>
      )}
      <GlobalTour />
    </BankAccountsInitialPopulation>
  );
};

export default PrivateRouter;
